import { useState, useEffect } from 'react';
import { LOGO_SMALL } from '@/assets/logo';
import { Menu, X, ArrowRight } from 'lucide-react';

const navLinks = [
  { label: 'Services', href: '#services' },
  { label: 'Process', href: '#process' },
  { label: 'Results', href: '#results' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Why Us', href: '#why-us' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-vertex-900/95 backdrop-blur-xl shadow-2xl shadow-vertex-950/50 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#hero" onClick={(e) => handleNavClick(e, '#hero')} className="flex items-center gap-3 group">
            <img
              src={LOGO_SMALL}
              alt="Vertex Marketing Solutions"
              className="h-10 w-10 rounded-lg transition-transform duration-300 group-hover:scale-105"
              data-testid="navbar-logo"
            />
            <div className="hidden sm:block">
              <span className="text-white font-display font-bold text-lg tracking-tight">VERTEX</span>
              <span className="text-vertex-400 font-display font-medium text-xs block -mt-1 tracking-widest">MARKETING</span>
            </div>
          </a>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-gray-300 hover:text-white font-medium text-sm transition-colors duration-300 relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-vertex-500 transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <a
              href="#cta"
              onClick={(e) => handleNavClick(e, '#cta')}
              data-testid="navbar-cta"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-vertex-500 hover:bg-vertex-600 text-white font-semibold text-sm rounded-lg transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-vertex-500/25 hover:shadow-vertex-500/40"
            >
              Book Strategy Call
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            mobileOpen ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-vertex-900/95 backdrop-blur-xl rounded-2xl p-6 border border-white/10 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block text-gray-300 hover:text-white hover:bg-white/5 font-medium text-base py-3 px-4 rounded-xl transition-all duration-300"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-4">
              <a
                href="#cta"
                onClick={(e) => handleNavClick(e, '#cta')}
                className="block text-center px-6 py-3 bg-vertex-500 hover:bg-vertex-600 text-white font-semibold rounded-xl transition-all duration-300"
              >
                Book Strategy Call
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
